/** Settings sheet: profile, export format, markup size, gestures, sign out. */
import { formatCode, type UserInfo } from '../../shared/api.ts';
import { ELEV_PREFIX_DEFAULT, INITIALS_RE, TEST_STYLES, type TestStyle } from '../../shared/doc.ts';
import { elevColourPicker } from './elevcolour.ts';

/** The words a new map's tags may start with (any other is a tap away on a map: Tag, with Elevation in hand). */
const ELEV_DEFAULT_WORDS: [string, string][] = [
  ['Elev.', 'Elev.'],
  ['EL', 'EL'],
  ['BOR', 'BOR'],
  ['BOE', 'BOE'],
  ['', 'None'],
];
import { testName } from '../model/testid.ts';
import { apiJson, describeError } from '../app/api.ts';
import { app, setSession, updateSettings, type AppState } from '../app/state.ts';
import { clearLocalData, requestSync } from '../app/sync.ts';
import { restyleTests } from '../app/maps.ts';
import { navigate } from '../app/router.ts';
import { append, clear, h, haptic, icon, ICONS, toast, segmented } from './dom.ts';
import { confirmSheet, openSheet } from './sheet.ts';

export function openSettings(): void {
  // The one place sync is spoken of: a line that says all is well, or what is waiting and why.
  const cloud = h('div', { class: 'cloud-line', role: 'status' });
  const renderCloud = (a: AppState) => {
    const waiting = a.maps.filter((m) => m.dirty || !m.imageUploaded).length;
    const maps = `${waiting} map${waiting === 1 ? '' : 's'}`;
    const trouble = a.sync === 'offline' || a.sync === 'error';
    clear(cloud);
    cloud.className = 'cloud-line ' + (trouble ? 'waiting' : a.sync === 'synced' ? 'ok' : '');
    append(cloud, [
      icon(trouble ? ICONS.cloudOff : a.sync === 'synced' ? ICONS.cloudCheck : ICONS.cloudUp, 22),
      h('span', { class: 'grow' }, trouble ? (waiting ? `${maps} will go to the cloud when it can be reached. They are safe on this device.` : 'The cloud cannot be reached just now.') : a.sync === 'synced' ? 'Every map is in the cloud.' : 'Saving to the cloud…'),
      trouble ? h('button', { class: 'btn subtle', type: 'button', onClick: () => requestSync('now') }, 'Try now') : null,
    ]);
  };
  const unsubscribe = app.subscribe((a, prev) => {
    if (a.sync !== prev.sync || a.maps !== prev.maps) renderCloud(a);
  });
  renderCloud(app.state);
  const st = app.state;
  const user = st.session?.user;

  const name = h('input', { class: 'input', type: 'text', value: user?.name ?? '', autocapitalize: 'words', placeholder: 'Your name', 'aria-label': 'Name', enterkeyhint: 'done' });
  const initials = h('input', { class: 'input mono narrow', type: 'text', value: user?.initials ?? '', autocapitalize: 'characters', maxlength: 4, placeholder: 'RB', 'aria-label': 'Initials', enterkeyhint: 'done' });
  // The name and initials are kept as they are typed, as a system setting is: once a field is left
  // (or the sheet closed) with something new in it. Only a problem is ever said.
  let kept = { name: user?.name ?? '', initials: user?.initials ?? '' };
  const saveProfile = async () => {
    if (!app.state.session) return; // (signed out)
    const want = { name: name.value.trim() || kept.name, initials: initials.value.trim().toUpperCase() };
    if (want.name === kept.name && want.initials === kept.initials) return;
    if (!INITIALS_RE.test(want.initials)) {
      toast('Initials must be 1–4 letters', 'error');
      return;
    }
    const was = kept;
    kept = want;
    try {
      const u = await apiJson<UserInfo>('/api/me', { method: 'PATCH', body: JSON.stringify(want) });
      await setSession({ token: app.state.session!.token, user: u });
    } catch (e) {
      kept = was;
      toast(describeError(e), 'error');
    }
  };
  for (const input of [name, initials]) {
    input.addEventListener('change', () => void saveProfile());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') input.blur();
    });
  }
  const s = openSheet('Settings', {
    onClose: () => {
      unsubscribe();
      void saveProfile();
    },
  });

  const seg = segmented;
  /** One of several choices too many or too wide for a segmented control: chips that wrap, the chosen one filled. */
  const choice = <V extends string>(options: [V, string][], value: V, onPick: (v: V) => void) => {
    const group = h('div', { class: 'chips', role: 'group' });
    for (const [v, label] of options)
      group.append(h('button', { class: 'chip', type: 'button', 'aria-pressed': String(v === value), onClick: (e: Event) => {
        for (const b of group.querySelectorAll('.chip')) b.setAttribute('aria-pressed', String(b === e.currentTarget));
        haptic();
        onPick(v);
      } }, label));
    return group;
  };
  const toggle = (checked: boolean, onChange: (v: boolean) => void) => {
    const b = h('button', { class: 'switch', type: 'button', role: 'switch', 'aria-checked': String(checked) });
    b.addEventListener('click', () => {
      const v = b.getAttribute('aria-checked') !== 'true';
      b.setAttribute('aria-checked', String(v));
      onChange(v);
    });
    return b;
  };
  // on a phone a row with a wide control (a segmented one) puts it under its label; a very wide one is under it everywhere
  const row = (label: string, sub: string | null, control: HTMLElement, full = false) => h('div', { class: 'item' + (full ? ' full' : control.classList.contains('seg') ? ' stacked' : '') }, h('div', { class: 'grow' }, h('div', { class: 'name' }, label), sub ? h('div', { class: 'sub' }, sub) : null), control);

  append(s.body, [
    h('div', { class: 'field' }, h('label', null, 'Inspector'), h('div', { class: 'row' }, name, initials), h('p', { class: 'help' }, 'Initials appear in test IDs written with the date (e.g. 20260909_RB_4).')),
    h(
      'div',
      { class: 'list' },
      row('Appearance', null, seg([['system', 'System'], ['light', 'Light'], ['dark', 'Dark']], st.settings.appearance, (v) => void updateSettings({ appearance: v as 'system' | 'light' | 'dark' }))),
      row('Export format', 'PNG is sharpest for line work; JPEG is smaller.', seg([['png', 'PNG'], ['jpeg', 'JPEG']], st.settings.exportFormat, (v) => void updateSettings({ exportFormat: v as 'png' | 'jpeg' }))),
      row('Test badges', 'What each test\'s badge says, on every soils map.', choice(TEST_STYLES.map((v): [TestStyle, string] => [v, testName({ date: '20260922', initials: user?.initials ?? 'RB', n: 6 }, v)]), st.settings.testStyle, (v) => {
        void updateSettings({ testStyle: v });
        void restyleTests(v);
      }), true),
      // how elevation tags read on new soils maps: their colour (red, as Ricky draws them, unless chosen otherwise), word and unit
      row('Elevation tag colour on new maps', 'A bright blue unless you choose otherwise. To change one map\'s tags, choose Elevation on it and tap the colour in the strip.', elevColourPicker(st.settings.elevDefaults.color, (c) => void updateSettings({ elevDefaults: { ...app.state.settings.elevDefaults, color: c } })), true),
      row('Elevation tag word on new maps', 'Shown before each elevation: #1 Elev. 2795.65.', seg(ELEV_DEFAULT_WORDS, st.settings.elevDefaults.prefix ?? ELEV_PREFIX_DEFAULT, (v) => {
        const { prefix: _p, ...rest } = app.state.settings.elevDefaults;
        void updateSettings({ elevDefaults: v === ELEV_PREFIX_DEFAULT ? rest : { ...rest, prefix: v } });
      })),
      row('Elevations on new maps in', null, seg([['ft', 'Feet'], ['m', 'Metres']], st.settings.elevDefaults.unit === 'm' ? 'm' : 'ft', (v) => {
        const { unit: _u, ...rest } = app.state.settings.elevDefaults;
        void updateSettings({ elevDefaults: v === 'm' ? { ...rest, unit: 'm' } : rest });
      })),
      row('Markup size for new maps', 'Labels, markers and leaders scale together.', seg([['0.85', 'Small'], ['1', 'Normal'], ['1.2', 'Large']], String(st.settings.markupScale), (v) => void updateSettings({ markupScale: Number(v) }))),
      row('Legend on new maps', 'Adds a key to the exported image.', toggle(st.settings.legendDefault, (v) => void updateSettings({ legendDefault: v }))),
      row('Pinpoint dots on new maps', 'A dot where each test was taken and a ring where each elevation was shot; off, the lines simply end on their spots. One map\'s from More on that map.', toggle(st.settings.dots, (v) => void updateSettings({ dots: v }))),
      row('Finger pans after Pencil use', 'When you have used Apple Pencil, one finger pans and the Pencil draws.', toggle(st.settings.fingerPansAfterPen, (v) => void updateSettings({ fingerPansAfterPen: v }))),
      row('Snap points to the plan', 'Points you place or drag take hold of the plan\'s lines and corners. Snap on a shape\'s bar switches this too.', toggle(st.settings.snapAnchors, (v) => void updateSettings({ snapAnchors: v }))),
    ),
    st.session?.code
      ? h(
          'div',
          { class: 'field' },
          h('label', null, 'Your sign-in code'),
          h('div', { class: 'input code readout' }, formatCode(st.session.code)),
          h('p', { class: 'help' }, 'Use this same code to sign in on another iPad or computer. Keep it to yourself.'),
          h(
            'div',
            { class: 'chips' },
            h('button', { class: 'chip', type: 'button', onClick: () => void navigator.clipboard?.writeText(formatCode(st.session!.code!)).then(() => toast('Code copied', 'success')) }, icon(ICONS.copy, 18), 'Copy code'),
            navigator.share ? h('button', { class: 'chip', type: 'button', onClick: () => void navigator.share({ text: `MapMark sign-in code: ${formatCode(st.session!.code!)}\n${location.origin}/signin` }).catch(() => {}) }, icon(ICONS.share, 18), 'Send to another device') : null,
          ),
        )
      : null,
    cloud,
    h('p', { class: 'help' }, `Signed in as ${user?.name ?? '—'} · ${st.installed ? 'Home Screen app' : 'Browser'} · v${__APP_VERSION__}`),
    h(
      'button',
      {
        class: 'btn danger block',
        type: 'button',
        onClick: async () => {
          if (!(await confirmSheet('Sign out?', 'Maps stay in the cloud and your sign-in code keeps working. Anything not yet uploaded from this device will be lost.', 'Sign out'))) return;
          try {
            await apiJson('/api/auth/logout', { method: 'POST' });
          } catch {
            /* offline: token will simply expire server-side */
          }
          await clearLocalData();
          await setSession(null);
          s.close();
          navigate({ name: 'signin' }, true);
        },
      },
      'Sign out',
    ),
  ]);
}
